import React, { useState, useEffect, useCallback } from 'react';
import { 
  RefreshCw, 
  X, 
  Database, 
  Clock, 
  Zap, 
  Calendar,
  Rocket, 
  Check,
  AlertCircle,
  TrendingUp,
  AlertTriangle,
  Wrench
} from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';

/**
 * Smart Veeqo Sync Modal - Enterprise-Grade Incremental Sync
 * 
 * Features:
 * - Incremental sync (only new/updated orders)
 * - Date range sync (respects UI filters)
 * - Full historical sync (for first-time setup)
 * - Real-time progress tracking
 * - Auto-refresh UI after sync
 * - Re-enrich orders with COGS from Product Master
 */
const VeeqoSyncModal = ({ isOpen, onClose, onSyncComplete, dateRange }) => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [enrichmentStatus, setEnrichmentStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [reEnriching, setReEnriching] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [reEnrichProgress, setReEnrichProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch smart sync status when modal opens
  const loadSyncStatus = useCallback(async () => {
    if (!isOpen) return;
    
    try {
      setLoading(true);
      
      // Fetch sync status and enrichment status in parallel
      const [syncData, enrichmentData] = await Promise.all([
        api.get('/api/v1/veeqo/smart-sync-status', {
          headers: { 'X-Tenant-ID': 'tenant-123' }
        }).catch(() => null),
        api.get('/api/v1/veeqo/enrichment-status', {
          headers: { 'X-Tenant-ID': 'tenant-123' }
        }).catch(() => null)
      ]);
      
      if (syncData) {
        setSyncStatus(syncData);
      } else {
        // Fallback to old endpoint
        try {
          const fallbackData = await api.get('/api/v1/veeqo/sync-status', {
            headers: { 'X-Tenant-ID': 'tenant-123' }
          });
          setSyncStatus({
            total_orders: fallbackData.current_order_count,
            last_successful_sync: fallbackData.last_sync?.sync_date,
            orders_synced_last_run: fallbackData.last_sync?.total_synced || 0,
            ready_for_incremental: false
          });
        } catch (e) {
          console.error('Fallback also failed:', e);
        }
      }
      
      if (enrichmentData) {
        setEnrichmentStatus(enrichmentData);
      }
    } catch (error) {
      console.error('Failed to fetch status:', error);
    } finally {
      setLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    loadSyncStatus();
  }, [loadSyncStatus]);

  // Poll for background job status
  const pollJobStatus = useCallback(async (jobId, syncType) => {
    let pollCount = 0;
    const maxPolls = 300; // Max 10 minutes (2s interval)
    
    const poll = async () => {
      try {
        const jobStatus = await api.get(`/api/v1/veeqo/sync-job/${jobId}`, {
          headers: { 'X-Tenant-ID': 'tenant-123' }
        });
        
        setSyncProgress({
          type: syncType,
          message: jobStatus.message || `Syncing... ${jobStatus.progress}%`,
          progress: jobStatus.progress || 0,
          complete: jobStatus.status === 'completed',
          error: jobStatus.status === 'failed',
          result: jobStatus.status === 'completed' ? jobStatus : null
        });
        
        if (jobStatus.status === 'completed') {
          toast.success(`🎉 Sync Complete!`, {
            description: `${jobStatus.orders_synced} orders synced (${jobStatus.new_orders} new, ${jobStatus.updated_orders} updated)`
          });
          
          await loadSyncStatus();
          if (onSyncComplete) {
            onSyncComplete(jobStatus);
          }
          
          setTimeout(() => {
            onClose();
            setSyncProgress(null);
            setSyncing(false);
          }, 2000);
          return;
        }
        
        if (jobStatus.status === 'failed') {
          toast.error('Sync Failed', {
            description: jobStatus.error || 'Unknown error'
          });
          setSyncing(false);
          return;
        }
        
        // Continue polling
        pollCount++;
        if (pollCount < maxPolls) {
          setTimeout(poll, 2000); // Poll every 2 seconds
        } else {
          setSyncProgress({
            type: syncType,
            message: 'Sync is still running in the background. Check back later.',
            progress: jobStatus.progress || 0
          });
          setSyncing(false);
        }
        
      } catch (error) {
        console.error('Poll error:', error);
        pollCount++;
        if (pollCount < maxPolls) {
          setTimeout(poll, 3000); // Retry after 3s on error
        }
      }
    };
    
    poll();
  }, [loadSyncStatus, onSyncComplete, onClose]);

  const handleSmartSync = async (syncType) => {
    try {
      setSyncing(true);
      setSyncProgress({ 
        type: syncType, 
        message: `Starting ${syncType} sync...`,
        progress: 0
      });
      
      let params = new URLSearchParams();
      let endpoint = '/api/v1/veeqo/smart-sync';
      
      if (syncType === 'incremental') {
        // Uses last_sync_cursor automatically
        setSyncProgress({ 
          type: syncType, 
          message: 'Fetching orders updated since last sync...',
          progress: 10
        });
      } else if (syncType === 'date_range') {
        // Use the date range from parent component
        if (dateRange?.start && dateRange?.end) {
          params.append('start_date', dateRange.start);
          params.append('end_date', dateRange.end);
          setSyncProgress({ 
            type: syncType, 
            message: `Syncing orders from ${dateRange.start} to ${dateRange.end}...`,
            progress: 10
          });
        } else {
          // Default to last 30 days
          const endDate = new Date().toISOString().split('T')[0];
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - 30);
          params.append('start_date', startDate.toISOString().split('T')[0]);
          params.append('end_date', endDate);
          setSyncProgress({ 
            type: syncType, 
            message: 'Syncing last 30 days...',
            progress: 10
          });
        }
      } else if (syncType === 'full') {
        params.append('force_full', 'true');
        setSyncProgress({ 
          type: syncType, 
          message: 'Starting full historical sync (this may take 10-15 min)...',
          progress: 5
        });
      }
      
      // Execute sync - returns immediately for large syncs
      const result = await api.post(`${endpoint}?${params.toString()}`, {}, {
        headers: { 'X-Tenant-ID': 'tenant-123' },
        timeout: 120000  // 2 minutes - increased for large syncs that process immediately
      });
      
      // Check if it's a background job
      if (result.background && result.job_id) {
        // Background job started - poll for progress
        setSyncProgress({ 
          type: syncType, 
          message: result.message || 'Background sync started...',
          progress: 5
        });
        
        toast.info('🚀 Background Sync Started', {
          description: result.message
        });
        
        // Start polling
        pollJobStatus(result.job_id, syncType);
        return;
      }
      
      // Immediate sync completed
      setSyncProgress({ 
        type: syncType, 
        message: `✅ Synced ${result.orders_synced} orders (${result.new_orders} new, ${result.updated_orders} updated)`,
        progress: 100,
        complete: true,
        result
      });
      
      toast.success(`🎉 Sync Complete!`, {
        description: `${result.orders_synced} orders synced`
      });
      
      // Refresh status
      await loadSyncStatus();
      
      // Notify parent to refresh data
      if (onSyncComplete) {
        onSyncComplete(result);
      }
      
      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose();
        setSyncProgress(null);
        setSyncing(false);
      }, 2000);
      
    } catch (error) {
      console.error('Sync failed:', error);
      setSyncProgress({ 
        type: syncType, 
        message: error.response?.data?.detail || error.message || 'Sync failed',
        error: true
      });
      toast.error('Sync Failed', {
        description: error.response?.data?.detail || error.message
      });
      setSyncing(false);
    }
  };

  // Poll for background enrichment job status
  const pollEnrichmentJobStatus = useCallback(async (jobId) => {
    let pollCount = 0;
    const maxPolls = 600; // Max 20 minutes (2s interval)
    
    const poll = async () => {
      try {
        const jobStatus = await api.get(`/api/v1/veeqo/enrichment-job/${jobId}`, {
          headers: { 'X-Tenant-ID': 'tenant-123' }
        });
        
        setReEnrichProgress({
          message: jobStatus.message || `Enriching... ${jobStatus.progress}%`,
          progress: jobStatus.progress || 0,
          complete: jobStatus.status === 'completed',
          error: jobStatus.status === 'failed',
          result: jobStatus.status === 'completed' ? jobStatus : null
        });
        
        if (jobStatus.status === 'completed') {
          toast.success(`🎉 Re-enrichment Complete!`, {
            description: `${jobStatus.enriched} orders updated, ${jobStatus.cogs_found} COGS found`
          });
          
          await loadSyncStatus();
          if (onSyncComplete) {
            onSyncComplete(jobStatus);
          }
          
          setTimeout(() => {
            setReEnrichProgress(null);
            setReEnriching(false);
          }, 3000);
          return;
        }
        
        if (jobStatus.status === 'failed') {
          toast.error('Re-enrichment Failed', {
            description: jobStatus.error || 'Unknown error'
          });
          setReEnriching(false);
          return;
        }
        
        // Continue polling
        pollCount++;
        if (pollCount < maxPolls) {
          setTimeout(poll, 2000); // Poll every 2 seconds
        } else {
          setReEnrichProgress({
            message: 'Enrichment is still running in the background. Check back later.',
            progress: jobStatus.progress || 0
          });
          setReEnriching(false);
        }
        
      } catch (error) {
        console.error('Enrichment poll error:', error);
        pollCount++;
        if (pollCount < maxPolls) {
          setTimeout(poll, 3000); // Retry after 3s on error
        }
      }
    };
    
    poll();
  }, [loadSyncStatus, onSyncComplete]);

  // Re-enrich orders with COGS from Product Master
  const handleReEnrich = async () => {
    try {
      setReEnriching(true);
      setReEnrichProgress({ 
        message: 'Starting COGS re-enrichment...',
        progress: 5
      });
      
      // Call re-enrich endpoint - now returns immediately with job_id
      const result = await api.post('/api/v1/veeqo/re-enrich?limit=10000&force=false', {}, {
        headers: { 'X-Tenant-ID': 'tenant-123' },
        timeout: 120000  // 2 minutes - increased for large operations
      });
      
      // Check if it's a background job
      if (result.background && result.job_id) {
        // Background job started - poll for progress
        setReEnrichProgress({ 
          message: result.message || 'Background enrichment started...',
          progress: 5
        });
        
        toast.info('🔄 Background Enrichment Started', {
          description: `Processing ${result.total_orders} orders...`
        });
        
        // Start polling
        pollEnrichmentJobStatus(result.job_id);
        return;
      }
      
      // No orders needed enrichment
      if (result.total_orders === 0) {
        setReEnrichProgress({ 
          message: '✅ All orders already have COGS data',
          progress: 100,
          complete: true
        });
        
        toast.success('All orders up to date!', {
          description: 'No orders needed COGS enrichment'
        });
        
        setTimeout(() => {
          setReEnrichProgress(null);
          setReEnriching(false);
        }, 2000);
        return;
      }
      
      // Immediate completion (shouldn't happen with new code, but handle just in case)
      setReEnrichProgress({ 
        message: `✅ Updated ${result.enriched || 0} orders with COGS data`,
        progress: 100,
        complete: true,
        result
      });
      
      toast.success('🎉 Re-enrichment Complete!', {
        description: `${result.enriched || 0} orders updated`
      });
      
      // Refresh statuses
      await loadSyncStatus();
      
      // Notify parent to refresh data
      if (onSyncComplete) {
        onSyncComplete(result);
      }
      
      // Clear progress after 3 seconds
      setTimeout(() => {
        setReEnrichProgress(null);
        setReEnriching(false);
      }, 3000);
      
    } catch (error) {
      console.error('Re-enrich failed:', error);
      setReEnrichProgress({ 
        message: error.response?.data?.detail || error.message || 'Re-enrichment failed',
        error: true
      });
      toast.error('Re-enrichment Failed', {
        description: error.response?.data?.detail || error.message
      });
      setReEnriching(false);
    }
  };

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg p-4 max-w-md w-full border border-slate-700 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <RefreshCw className={`w-4 h-4 text-blue-400 ${syncing ? 'animate-spin' : ''}`} />
            Smart Sync
          </h3>
          <button
            onClick={() => {
              if (!syncing) {
                onClose();
                setSyncProgress(null);
              }
            }}
            disabled={syncing}
            className="text-gray-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Status Box */}
        {loading ? (
          <div className="bg-slate-900 rounded p-3 mb-3 animate-pulse">
            <div className="h-4 bg-slate-700 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-slate-700 rounded w-1/2"></div>
          </div>
        ) : (
          <div className="bg-slate-900 rounded p-3 mb-3">
            <div className="text-xs text-gray-400 space-y-2">
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-1.5">
                  <Database className="w-3 h-3" />
                  Total Orders in DB:
                </span>
                <span className="text-white font-semibold text-sm">
                  {syncStatus?.total_orders?.toLocaleString() || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />
                  Last Sync:
                </span>
                <span className="text-white text-[11px]">
                  {formatDate(syncStatus?.last_successful_sync)}
                </span>
              </div>
              {syncStatus?.orders_synced_last_run > 0 && (
                <div className="flex items-center gap-1.5 text-green-400 text-[11px]">
                  <Check className="w-3 h-3" />
                  {syncStatus.orders_synced_last_run} orders synced ({syncStatus.sync_type_last_run})
                </div>
              )}
              {syncStatus?.ready_for_incremental && (
                <div className="flex items-center gap-1.5 text-emerald-400 text-[11px]">
                  <TrendingUp className="w-3 h-3" />
                  Ready for incremental sync
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Sync Options */}
        <div className="space-y-2 mb-3">
          {/* Incremental Sync - RECOMMENDED */}
          <button
            onClick={() => handleSmartSync('incremental')}
            disabled={syncing}
            className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded px-3 py-2.5 text-left transition-colors group border-2 border-emerald-500"
          >
            <div className="font-semibold text-xs flex items-center gap-1.5">
              <Zap className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" />
              ⚡ Incremental Sync
              <span className="ml-auto text-[10px] bg-emerald-800 px-1.5 py-0.5 rounded">RECOMMENDED</span>
            </div>
            <div className="text-[10px] text-emerald-200 ml-5.5 mt-0.5">
              Only new/updated orders since last sync • Fast ~10-30s
            </div>
          </button>
          
          {/* Date Range Sync */}
          <button
            onClick={() => handleSmartSync('date_range')}
            disabled={syncing}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded px-3 py-2 text-left transition-colors group"
          >
            <div className="font-semibold text-xs flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 group-hover:scale-110 transition-transform duration-200" />
              Date Range Sync
            </div>
            <div className="text-[10px] text-blue-200 ml-5 mt-0.5">
              {dateRange?.start && dateRange?.end 
                ? `${dateRange.start} to ${dateRange.end}` 
                : 'Last 30 days'} • ~1-3 min
            </div>
          </button>
          
          {/* Full Historical Sync */}
          <button
            onClick={() => handleSmartSync('full')}
            disabled={syncing}
            className="w-full bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:cursor-not-allowed text-gray-300 rounded px-3 py-2 text-left transition-colors group border border-slate-600"
          >
            <div className="font-semibold text-xs flex items-center gap-1.5">
              <Rocket className="w-3.5 h-3.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
              Full Historical Sync
            </div>
            <div className="text-[10px] text-gray-400 ml-5 mt-0.5">
              All orders from beginning • ~10-15 min
            </div>
          </button>
        </div>
        
        {/* Progress Message */}
        {syncProgress && (
          <div className={`px-3 py-2 rounded text-xs ${
            syncProgress.error 
              ? 'bg-red-900/30 border border-red-600/30 text-red-300' 
              : syncProgress.complete 
                ? 'bg-green-900/30 border border-green-600/30 text-green-300'
                : 'bg-blue-900/30 border border-blue-600/30 text-blue-300'
          }`}>
            <div className="flex items-center gap-2">
              {syncProgress.error ? (
                <AlertCircle className="w-4 h-4" />
              ) : syncProgress.complete ? (
                <Check className="w-4 h-4" />
              ) : (
                <RefreshCw className="w-4 h-4 animate-spin" />
              )}
              <span>{syncProgress.message}</span>
            </div>
            {!syncProgress.error && !syncProgress.complete && syncProgress.progress > 0 && (
              <div className="mt-2 bg-slate-700 rounded-full h-1.5 overflow-hidden">
                <div 
                  className="bg-blue-500 h-full transition-all duration-300"
                  style={{ width: `${syncProgress.progress}%` }}
                />
              </div>
            )}
          </div>
        )}
        
        {/* Re-enrich COGS Section */}
        {enrichmentStatus && enrichmentStatus.missing_cogs > 0 && (
          <div className="mt-3 border-t border-slate-700 pt-3">
            <div className="bg-amber-900/20 border border-amber-500/30 rounded p-3 mb-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-xs text-amber-300 font-semibold">
                    {enrichmentStatus.missing_cogs.toLocaleString()} orders have $0 COGS
                  </div>
                  <div className="text-[10px] text-amber-400/80 mt-0.5">
                    Click below to update these orders with COGS from Product Master
                  </div>
                </div>
              </div>
            </div>
            
            <button
              onClick={handleReEnrich}
              disabled={syncing || reEnriching}
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded px-3 py-2 text-left transition-colors group"
            >
              <div className="font-semibold text-xs flex items-center gap-1.5">
                <Wrench className={`w-3.5 h-3.5 ${reEnriching ? 'animate-spin' : 'group-hover:rotate-12'} transition-transform duration-200`} />
                Re-enrich Orders with COGS
                {reEnriching && <span className="ml-auto text-[10px]">Processing...</span>}
              </div>
              <div className="text-[10px] text-orange-200 ml-5 mt-0.5">
                Update {enrichmentStatus.missing_cogs.toLocaleString()} orders from Product Master • ~1-3 min
              </div>
            </button>
            
            {/* Re-enrich Progress */}
            {reEnrichProgress && (
              <div className={`mt-2 px-3 py-2 rounded text-xs ${
                reEnrichProgress.error 
                  ? 'bg-red-900/30 border border-red-600/30 text-red-300' 
                  : reEnrichProgress.complete 
                    ? 'bg-green-900/30 border border-green-600/30 text-green-300'
                    : 'bg-orange-900/30 border border-orange-600/30 text-orange-300'
              }`}>
                <div className="flex items-center gap-2">
                  {reEnrichProgress.error ? (
                    <AlertCircle className="w-4 h-4" />
                  ) : reEnrichProgress.complete ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  )}
                  <span>{reEnrichProgress.message}</span>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Enrichment Stats */}
        {enrichmentStatus && enrichmentStatus.missing_cogs === 0 && (
          <div className="mt-3 border-t border-slate-700 pt-3">
            <div className="bg-emerald-900/20 border border-emerald-500/30 rounded p-2 flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-emerald-300">
                All {enrichmentStatus.total_orders.toLocaleString()} orders have COGS data ✓
              </span>
            </div>
          </div>
        )}
        
        {/* Help Text */}
        <div className="mt-3 text-[10px] text-gray-500 border-t border-slate-700 pt-2">
          <strong>💡 Tip:</strong> Use Incremental Sync for daily updates. Only use Full Sync for initial setup or recovery.
        </div>
      </div>
    </div>
  );
};

export default VeeqoSyncModal;
